import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Card from "../components/Card";
import { supabase } from "../lib/supabase";
import "./homepage.css";

const SUPPORT_EMAIL = 'help@makersstuff.com';
const SHOPIFY_APP_MARKET_URL = 'https://apps.shopify.com/makersstuff';

const tiles = [
  {
    name: "StoreStuff",
    desc: "Raw materials. Clearly tracked.",
    icon: "🧱",
    image: "/assets/images/StoreStuff.png",
    imageWebp: "/assets/images/StoreStuff.webp",
    imageWidth: 638,
    imageHeight: 825,
    link: "/raw-materials-inventory",
    expanded: "StoreStuff is your materials ledger. It shows exactly what you physically have on the shelf. Quantities only change when real actions are recorded elsewhere — no silent edits, no drift.",
  },
  {
    name: "PlanStuff",
    desc: "Define what you make.",
    icon: "🧾",
    image: "/assets/images/PlanStuff.png",
    imageWebp: "/assets/images/PlanStuff.webp",
    imageWidth: 540,
    imageHeight: 540,
    link: "/bom-editor",
    expanded: "PlanStuff is where recipes live. It defines the materials and quantities per unit without moving inventory or creating history. Planning stays separate from execution.",
  },
  {
    name: "DoStuff",
    desc: "A record of what you made.",
    icon: "🛠️",
    image: "/assets/images/DoStuff.png",
    imageWebp: "/assets/images/DoStuff.webp",
    imageWidth: 540,
    imageHeight: 540,
    featured: true,
    link: "/manufacturing-build",
    expanded: "DoStuff is where builds, adjustments, and receiving occur. Inventory changes only when something real happens — and every action is logged with context.",
  },
  {
    name: "SellStuff",
    desc: "Finished goods with real margins.",
    icon: "🏷️",
    image: "/assets/images/SellStuff.png",
    imageWebp: "/assets/images/SellStuff.webp",
    imageWidth: 540,
    imageHeight: 540,
    link: "/products-and-variants",
    expanded: "SellStuff reflects products and variants built from your recipes. Costs and margins are calculated from real material and build data — not manual estimates.",
  },
  {
    name: "BuyStuff",
    desc: "Purchasing with traceability.",
    icon: "📦",
    image: "/assets/images/BuyStuff.png",
    imageWebp: "/assets/images/BuyStuff.webp",
    imageWidth: 540,
    imageHeight: 540,
    link: "/purchase-orders",
    expanded: "BuyStuff handles purchase orders and receiving, tracking commitments and updating inventory. Vendor data—including supplier details, terms, and history—remains organized and linked to orders.",
  },
  {
    name: "RecordStuff",
    desc: "Every action recorded - Clear Transparency.",
    icon: "🧠",
    image: "/assets/images/RecordStuff.png",
    imageWebp: "/assets/images/RecordStuff.webp",
    imageWidth: 540,
    imageHeight: 540,
    link: "/audit-trail",
    expanded: "RecordStuff preserves builds, adjustments, and purchases exactly as they occurred. Records are permanent and read-only — history isn't rewritten.",
  },
  {
    name: "PeopleStuff",
    desc: "Who can do what in your system.",
    icon: "👥",
    image: "/assets/images/PeopleStuff.png",
    imageWebp: "/assets/images/PeopleStuff.webp",
    imageWidth: 540,
    imageHeight: 540,
    link: "/team-management",
    expanded: "PeopleStuff controls who can plan, build, buy, or adjust. Access is structured without compromising operational integrity.",
  },
];

const pricingPlans = [
  { name: 'Workbench', price: '$9/mo', variants: 'Up to 50 variants' },
  { name: 'Dreamer', price: '$29/mo', variants: 'Up to 2,500 variants' },
  { name: 'Maker', price: '$39/mo', variants: 'Up to 5,000 variants', featured: true },
  { name: 'Studio', price: '$89/mo', variants: 'Up to 20,000 variants' },
];

export default function HomePage() {
  const CONTACT_SUBMIT_TIMEOUT_MS = 15000;
  const [showContact, setShowContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", shop: "", message: "" });
  const [contactStatus, setContactStatus] = useState(null); // 'sending' | 'sent' | 'error'
  const [contactError, setContactError] = useState('');
  const [flippedTile, setFlippedTile] = useState(null);
  const [checkoutNotice, setCheckoutNotice] = useState(null);

  // Click outside any tile resets to front
  const handlePageClick = useCallback((e) => {
    if (flippedTile && !e.target.closest('.hp-flip-container')) {
      setFlippedTile(null);
    }
  }, [flippedTile]);

  useEffect(() => {
    document.addEventListener('click', handlePageClick);
    return () => document.removeEventListener('click', handlePageClick);
  }, [handlePageClick]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      setCheckoutNotice({
        type: 'success',
        message: 'Stuff happened. Your account is ready. Log in to continue.',
      });
    } else if (checkout === 'canceled') {
      setCheckoutNotice({
        type: 'info',
        message: 'Checkout was canceled. Sign in anytime to resume billing setup.',
      });
    } else {
      setCheckoutNotice(null);
    }
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus("sending");
    setContactError('');
    try {
      const { error } = await supabase
        .from("contact_messages")
        .insert({
          name: contactForm.name,
          email: contactForm.email,
          subject: contactForm.shop,
          message: contactForm.message,
        })
        .abortSignal(AbortSignal.timeout(CONTACT_SUBMIT_TIMEOUT_MS));

      if (error) throw error;

      setContactStatus("sent");
      setContactForm({ name: "", email: "", shop: "", message: "" });
      setTimeout(() => {
        setShowContact(false);
        setContactStatus(null);
        setContactError('');
      }, 2500);
    } catch (error) {
      const message = error?.message || '';
      const didTimeout = message.toLowerCase().includes('timed out') || message.toLowerCase().includes('abort');

      setContactStatus("error");

      if (didTimeout) {
        setContactError('Request timed out. Please try again in a moment.');
      } else {
        setContactError('Something went wrong while sending your request. Please try again.');
      }

      if (import.meta.env.DEV) {
        console.error('[homepage] contact submit failed:', error);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>MakersStuff | Know Your Stuff</title>
        <link
          rel="preload"
          as="image"
          href="/assets/images/HeadderImageFade.webp"
          type="image/webp"
          fetchpriority="high"
        />
      </Helmet>
    <div className="hp-page">
      {/* Top Nav — minimal: just Login + Contact */}
      <header className="hp-topbar">
        <div className="hp-container hp-topbar-inner">
          <Link className="hp-brand" to="/" aria-label="MakersStuff Home">
            <span className="hp-brand-text">MakersStuff</span>
          </Link>

          <div className="hp-actions">
            <Link className="hp-btn subtle" to="/pricing">
              Pricing
            </Link>
            <button
              className="hp-btn ghost"
              onClick={() => setShowContact(true)}
            >
              Contact Us
            </button>
            <Link className="hp-btn primary" to="/login">
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {checkoutNotice && (
        <div className="hp-container mt-4">
          <div
            className={checkoutNotice.type === 'success'
              ? 'bg-success/10 border-2 border-success/30 text-success px-6 py-5 rounded-xl text-base md:text-lg font-semibold shadow-elevation-2'
              : 'bg-muted border border-border text-foreground px-4 py-3 rounded-lg text-sm'}
          >
            {checkoutNotice.message}
          </div>
        </div>
      )}

      {/* Hero — full-width image with overlaid copy */}
      <section className="hp-hero">
        <div className="hp-hero-bg">
          <picture className="hp-hero-bg-picture">
            <source srcSet="/assets/images/HeadderImageFade.webp" type="image/webp" />
            <img
              src="/assets/images/HeadderImageFade.png"
              alt=""
              className="hp-hero-bg-img"
              width={2400}
              height={1792}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="hp-hero-bg-overlay" />
        </div>
        <div className="hp-container hp-hero-inner">
          <div className="hp-hero-logo-col">
            <picture>
              <source srcSet="/assets/images/logo.webp" type="image/webp" />
              <img
                className="hp-hero-logo"
                src="/assets/images/logo.png"
                alt="MakersStuff"
                width={1922}
                height={1283}
                decoding="async"
              />
            </picture>
          </div>
          <div className="hp-hero-copy">
            <h1>Know Your Stuff.</h1>
            <p className="hp-lead">
              Built for Makers. MakersStuff brings structure to your materials,
              builds, and pricing — so your shop runs as clean as your craft.
            </p>

            <div className="hp-cta-row">
              <a className="hp-btn ghost" href="#system">
                See how the WorkflowStuff works.
              </a>
              <Link className="hp-btn primary" to="/onboarding">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Pain */}
      <section className="hp-section hp-section-audience">
        <div className="hp-container">
          <h2 className="hp-section-heading-with-label">
            Spreadsheets break when production gets real.
          </h2>

          <div className="hp-pain-grid">
            <Card className="hp-pain-card">
              <div className="hp-pain-title">Drift</div>
              <p>
                Counts drift. Stuff disappears. You stop trusting your numbers.  
              </p>
            </Card>
            <Card className="hp-pain-card">
              <div className="hp-pain-title">Guessing</div>
              <p>
                Batch costs turn into estimates instead of facts. 
              </p>
            </Card>
            <Card className="hp-pain-card">
              <div className="hp-pain-title">Chaos</div>
              <p>
                Tracking gets skipped when you're busy — and that's when
                mistakes cost the most. 
              </p>
            </Card>
          </div>

          <p className="hp-bridge hp-center">
            MakersStuff simplifies the complexity — it doesn't ignore it.
          </p>

          <div className="hp-audience hp-center">
            <h3 className="hp-audience-title">Built with Makers in Mind</h3>
            <p className="hp-audience-heading">
              MakersStuff tracks the stuff that makes stuff happen.
            </p>
            <p className="hp-audience-desc" style={{ maxWidth: '38em', margin: '0.75em auto' }}>
              It connects your materials, builds, and finished goods so small-batch
              makers can run their shops with clarity.
            </p>
            <p className="hp-audience-desc" style={{ maxWidth: '38em', margin: '0.75em auto', fontSize: '0.95em', opacity: 0.85 }}>
              Designed for makers, artisans, and small production shops who have
              outgrown spreadsheets but don&rsquo;t need the complexity of enterprise
              MRP&nbsp;systems.
            </p>
            <div className="hp-beta-gallery">
              <picture className="hp-beta-picture">
                <source srcSet="/assets/images/Beta1.webp" type="image/webp" />
                <img
                  src="/assets/images/Beta1.png"
                  alt="Workshop"
                  className="hp-beta-img"
                  width={638}
                  height={825}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <picture className="hp-beta-picture">
                <source srcSet="/assets/images/Beta2.webp" type="image/webp" />
                <img
                  src="/assets/images/Beta2.png"
                  alt="Materials"
                  className="hp-beta-img"
                  width={638}
                  height={825}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <picture className="hp-beta-picture">
                <source srcSet="/assets/images/Beta3.webp" type="image/webp" />
                <img
                  src="/assets/images/Beta3.png"
                  alt="Craft"
                  className="hp-beta-img"
                  width={638}
                  height={825}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <picture className="hp-beta-picture">
                <source srcSet="/assets/images/Beta4.webp" type="image/webp" />
                <img
                  src="/assets/images/Beta4.png"
                  alt="Production"
                  className="hp-beta-img"
                  width={540}
                  height={540}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        </div>

      </section>

      <section className="hp-section" id="pricing-home">
        <div className="hp-container">
          <div className="hp-shopify-badge-wrap">
            <a
              className="hp-shopify-badge-link"
              href={SHOPIFY_APP_MARKET_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Approved by Shopify. Open MakersStuff listing in the Shopify App Store"
            >
              <picture className="hp-shopify-badge-picture">
                <source srcSet="/assets/images/1shopify%20approved-01.webp" type="image/webp" />
                <img
                  className="hp-shopify-badge"
                  src="/assets/images/1shopify%20approved-01.png"
                  alt="Approved by Shopify"
                  width={1117}
                  height={1381}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </a>
          </div>

          <h2>Pricing</h2>
          <p className="hp-sub">
            All plans include all features and unlimited users. Plans are tiered by variants.
          </p>

          <div className="hp-pricing-grid">
            {pricingPlans.map((plan) => (
              <Card key={plan.name} className={`hp-pricing-card ${plan.featured ? 'hp-pricing-card-featured' : ''}`}>
                {plan.featured && <div className="hp-pricing-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <div className="hp-pricing-price">{plan.price}</div>
                <p className="hp-pricing-variants">{plan.variants}</p>
                <p className="hp-pricing-note">All features included</p>
                <p className="hp-pricing-note">Unlimited users (reasonable use applies)</p>
                <Link className="hp-btn ghost" to="/pricing">
                  Subscribe
                </Link>
              </Card>
            ))}
          </div>

          <p className="hp-pricing-policy">
            Overage billing is optional: team admins can bill $0.04 per extra variant at month end, or disable overage billing to pause variants above cap. See our Terms for full details.
          </p>
        </div>
      </section>

      {/* SECTION 3 — System Overview */}
      <section className="hp-section hp-section-alt" id="system">
        <div className="hp-container">
          <h2>The Stuff that Makes Stuff Happen — Your Operational Hub.</h2>

          <div className="hp-tiles">
            {tiles.map((t) => {
              const isFlipped = flippedTile === t.name;
              return (
                <div
                  key={t.name}
                  className={`hp-flip-container ${isFlipped ? "hp-flipped" : ""} ${t.featured ? "hp-tile-featured" : ""}`}
                  onClick={() => setFlippedTile(isFlipped ? null : t.name)}
                  role="button"
                  tabIndex={0}
                  aria-label={`${t.name} — ${t.desc}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setFlippedTile(isFlipped ? null : t.name);
                    }
                  }}
                >
                  <div className="hp-flip-inner">
                    {/* Back (rendered first so front stacks on top) */}
                    <Card className={`hp-flip-face hp-flip-back hp-tile ${t.featured ? "hp-tile-featured" : ""}`}>
                      <div className="hp-tile-top">
                        <div className="hp-tile-icon" aria-hidden="true">
                          {t.icon}
                        </div>
                        <div className="hp-tile-name">{t.name}</div>
                      </div>
                      <div className="hp-tile-expanded">{t.expanded}</div>
                    </Card>

                    {/* Front (last in DOM = renders on top in 3D context) */}
                    <Card className={`hp-flip-face hp-flip-front hp-tile ${t.featured ? "hp-tile-featured" : ""}`}>
                      <div className="hp-tile-top">
                        <div className="hp-tile-icon" aria-hidden="true">
                          {t.icon}
                        </div>
                        <div className="hp-tile-name">{t.name}</div>
                      </div>
                      <div className="hp-tile-desc">{t.desc}</div>
                      {t.image && (
                        <picture>
                          {t.imageWebp && <source srcSet={t.imageWebp} type="image/webp" />}
                          <img
                            src={t.image}
                            alt=""
                            className="hp-tile-sticker"
                            aria-hidden="true"
                            width={t.imageWidth || 540}
                            height={t.imageHeight || 540}
                            loading="lazy"
                            decoding="async"
                          />
                        </picture>
                      )}
                      <div className="hp-tile-tap-hint">more stuff &rsaquo;</div>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What it is / isn't */}
      <section className="hp-section">
        <div className="hp-container">
          <h2>What it is (and isn&rsquo;t)</h2>
          <p className="hp-sub">
            MakersStuff is the operating system for small-batch makers. A single
            source of truth for materials, builds, costs, and history.
          </p>

          <div className="hp-two-col">
            <Card className="hp-split-is">
              <div className="hp-split-head">
                <span className="hp-icon hp-icon-ok" aria-hidden="true">
                  ✓
                </span>
                <h3>MakersStuff is</h3>
              </div>
              <ul className="hp-list hp-list-spaced">
                <li>
                  <strong>No fluff</strong>
                  <span className="hp-list-detail">Clear actions, real data. No dashboard theater or vanity metrics.</span>
                </li>
                <li>
                  <strong>Honest inventory</strong>
                  <span className="hp-list-detail">Builds update stock automatically. No manual adjustments, no drift, no surprises.</span>
                </li>
                <li>
                  <strong>Trust the numbers</strong>
                  <span className="hp-list-detail">Capture what actually happened — so pricing, margins, and planning are based on facts.</span>
                </li>
                <li>
                  <strong>Built for makers</strong>
                  <span className="hp-list-detail">Designed around the way small-batch production actually works — not how enterprise software thinks it should.</span>
                </li>
              </ul>
            </Card>

            <Card className="hp-split-not">
              <div className="hp-split-head">
                <span className="hp-icon hp-icon-no" aria-hidden="true">
                  ⨯
                </span>
                <h3>MakersStuff is not</h3>
              </div>
              <ul className="hp-list hp-list-spaced">
                <li>
                  <strong>Accounting software</strong>
                  <span className="hp-list-detail">We don&rsquo;t do invoices, taxes, or payroll. Pair us with what you already use.</span>
                </li>
                <li>
                  <strong>Generic inventory tools</strong>
                  <span className="hp-list-detail">We&rsquo;re not a warehouse app. We track materials through recipes and builds.</span>
                </li>
                <li>
                  <strong>Enterprise ERP</strong>
                  <span className="hp-list-detail">No lengthy onboarding. No consultants. No unnecessary complexity.</span>
                </li>
              </ul>
            </Card>
          </div>

          <div className="hp-value-strip">
            <span>Intentionally Simple</span>
            <span className="dot">•</span>
            <span>Your System</span>
            <span className="dot">•</span>
            <span>Your Stuff Organized</span>
          </div>
        </div>
      </section>

      {/* Bottom image with fade */}
      <section className="hp-btm-image-section">
        <picture className="hp-btm-picture">
          <source srcSet="/assets/images/BtmImageFade.webp" type="image/webp" />
          <img
            src="/assets/images/BtmImageFade.png"
            alt=""
            className="hp-btm-img"
            width={3168}
            height={1344}
            loading="lazy"
            decoding="async"
          />
        </picture>
        <div className="hp-btm-fade" />
      </section>

      {/* Maker origin */}
      <section className="hp-section hp-section-alt">
        <div className="hp-container">
          <h2>Made by a Maker. Made for People.</h2>
          <p className="hp-sub">
        Because Making Things Is Hard Enough | Your software shouldn’t make it harder          
          </p>

          <div className="hp-principles">
            <Card className="hp-principle">
              <div className="hp-principle-title">No fluff</div>
              <p>Clear actions. No dashboard theater.</p>
            </Card>
            <Card className="hp-principle">
              <div className="hp-principle-title">Honest inventory</div>
              <p>
                Builds update stock automatically — no manual adjustments, no
                drift.
              </p>
            </Card>
            <Card className="hp-principle">
              <div className="hp-principle-title">Trust the numbers</div>
              <p>
                Capture what actually happened — so pricing and planning aren't
                guesses.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="hp-final-cta">
        <div className="hp-container hp-final-inner">
          <div>
            <h2>Run your shop with clarity.</h2>
            <p className="hp-sub">
              Start with your materials. Build with confidence. Keep the record
              clean.
            </p>
          </div>
          <div className="hp-cta-row">
            <Link className="hp-btn primary" to="/onboarding">
              Sign Up
            </Link>
            <a className="hp-btn ghost" href="#system">
              Explore the system
            </a>
          </div>
        </div>
      </section>

      <footer className="hp-footer">
        <div className="hp-container hp-footer-inner">
          <div>
            <div className="hp-foot-name">MakersStuff</div>
            <div className="hp-foot-small">
              The operating system for small-batch makers.
            </div>
          </div>

          <div className="hp-foot-links">
            <Link to="/pricing">Pricing</Link>
            <a href="#system">About</a>
            <a href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>

      {/* Contact Us Modal */}
      {showContact && (
        <div className="hp-modal-overlay" onClick={() => setShowContact(false)}>
          <div className="hp-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="hp-modal-close"
              onClick={() => setShowContact(false)}
              aria-label="Close"
            >
              ✕
            </button>

            <h2>Contact Us</h2>
            <p className="hp-sub">
              Questions about MakersStuff? We are here to help.
            </p>
            <p className="hp-sub hp-modal-detail">
              Tell us a bit about your shop, what you make, and what you need
              help with. We will get back to you as soon as possible.
            </p>

            {contactStatus === "sent" ? (
              <div className="hp-contact-success">
                <span aria-hidden="true">✓</span> Request sent! We'll be in
                touch.
              </div>
            ) : (
              <form className="hp-contact-form" onSubmit={handleContactSubmit}>
                <label className="hp-form-label">
                  Name
                  <input
                    className="hp-form-input"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </label>
                <label className="hp-form-label">
                  Email
                  <input
                    className="hp-form-input"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, email: e.target.value }))
                    }
                  />
                </label>
                <label className="hp-form-label">
                  What do you make?
                  <input
                    className="hp-form-input"
                    type="text"
                    value={contactForm.shop}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, shop: e.target.value }))
                    }
                  />
                </label>
                <label className="hp-form-label">
                  Tell us about your shop and how you currently track materials
                  &amp; builds
                  <textarea
                    className="hp-form-input hp-form-textarea"
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm((f) => ({
                        ...f,
                        message: e.target.value,
                      }))
                    }
                  />
                </label>

                {contactStatus === "error" && (
                  <p className="hp-contact-error">
                    {contactError || "Something went wrong — please try again."}
                  </p>
                )}

                <button
                  className="hp-btn primary"
                  type="submit"
                  disabled={contactStatus === "sending"}
                >
                  {contactStatus === "sending"
                    ? "Sending…"
                    : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
